import React, { useState, useEffect, useCallback } from 'react';
import { getTeamLeads, getCustomerDetails, getUserSubTeamInfo } from '../../api';
import CustomerDetail from '../CustomerDetail';
import { useNavigate } from 'react-router-dom';

function TeamLeads({ user, teamId, teamName, customerId, onBack }) {
  const [leads, setLeads] = useState([]);
  const [allLeads, setAllLeads] = useState([]); // Ungefilterte Leads
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId || null);
  const [availableServiceProviders, setAvailableServiceProviders] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [userSubTeamInfo, setUserSubTeamInfo] = useState(null);

  const navigate = useNavigate();

  // Filter State
  const [filters, setFilters] = useState({
    sortBy: 'erstellt', // erstellt, name
    sortOrder: 'desc', // desc, asc
    dateFrom: '',
    dateTo: '',
    statusPv: '', // Alle Status
    statusWp: '', // Alle Status
    dienstleister: '' // Dienstleister Filter
  });

  // Mobile Responsive Check
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reagiere auf Änderungen der customerId
  useEffect(() => {
    setSelectedCustomerId(customerId || null);
  }, [customerId]);

  // Responsive Styles - gleich wie in Leads.js
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px',
      padding: isMobile ? '5px' : '15px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '15px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.2rem' : '1.6rem',
      margin: 0,
    },
    filters: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '10px' : '15px',
      marginBottom: '15px',
    },
    filterRow: {
      display: 'flex',
      gap: isMobile ? '8px' : '15px',
      marginBottom: '12px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: isMobile ? 'auto' : '120px',
      flex: isMobile ? '1' : 'none',
    },
    filterLabel: {
      fontSize: '0.8rem',
      fontWeight: '500',
      color: '#4a5568',
      marginBottom: '4px',
    },
    filterInput: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem',
    },
    resetButton: {
      padding: '6px 12px',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      marginTop: isMobile ? '8px' : '15px',
      width: isMobile ? '100%' : 'auto',
    },
    tableContainer: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      maxHeight: '70vh',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '900px' : isTablet ? '1000px' : 'auto',
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.85rem',
    },
    th: {
      background: '#f7fafc',
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#4a5568',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    td: {
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      maxWidth: isMobile ? '80px' : isTablet ? '100px' : '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Specific column widths
    dateColumn: {
      minWidth: isMobile ? '70px' : '80px',
      maxWidth: isMobile ? '70px' : '80px',
    },
    productColumn: {
      minWidth: isMobile ? '50px' : '60px',
      maxWidth: isMobile ? '50px' : '60px',
    },
    nameColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '120px' : '150px',
    },
    emailColumn: {
      minWidth: isMobile ? '90px' : '120px',
      maxWidth: isMobile ? '120px' : '180px',
    },
    phoneColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    statusColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    projektColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '80px' : '120px',
    },
    dienstleisterColumn: {
      minWidth: isMobile ? '70px' : '90px',
      maxWidth: isMobile ? '90px' : '130px',
    },
    optInColumn: {
      minWidth: isMobile ? '50px' : '60px',
      maxWidth: isMobile ? '50px' : '60px',
    },
    row: {
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    statusBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.15rem 0.3rem' : '0.2rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: isMobile ? '0.55rem' : '0.65rem',
      fontWeight: '500',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      minWidth: isMobile ? '35px' : '50px',
      maxWidth: isMobile ? '60px' : '80px',
    },
    pvBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    wpBadge: {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    productBadges: {
      display: 'flex',
      gap: '2px',
      justifyContent: 'center',
    },
    productBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.1rem 0.2rem' : '0.15rem 0.3rem',
      borderRadius: '0.2rem',
      fontSize: isMobile ? '0.5rem' : '0.6rem',
      fontWeight: '600',
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#718096',
      fontSize: '1.1rem',
    },
    noData: {
      textAlign: 'center',
      padding: '40px',
      color: '#718096',
      fontSize: '1.1rem',
      fontStyle: 'italic',
    },
    error: {
      background: '#fed7d7',
      color: '#c53030',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '15px',
      fontSize: '0.9rem',
    },
    backButton: {
      padding: '6px 12px',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      marginTop: isMobile ? '8px' : '15px',
      width: isMobile ? '100%' : 'auto',
    },
    detailContent: {
      padding: '15px',
    },
  };

  // Sub-Team-Informationen laden
  const fetchUserSubTeamInfo = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const data = await getUserSubTeamInfo(user.id);
      if (data.success) {
        setUserSubTeamInfo(data.userInfo);
      }
    } catch (err) {
      console.error("Fehler beim Laden der Sub-Team-Informationen:", err);
    }
  }, [user?.id]);

  // Leads laden
  const fetchLeads = useCallback(async () => {
    if (!user?.id || !teamId) {
      setError('Benutzer oder Team-ID fehlt');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const data = await getTeamLeads(user.id, teamId);
      
      if (data.success) {
        if (Array.isArray(data.leads) && data.leads.length > 0) {
          setLeads(data.leads);
          setAllLeads(data.leads);
          
          // Verfügbare Dienstleister extrahieren
          const providers = [...new Set(data.leads.map(lead => lead.dienstleister).filter(Boolean))];
          setAvailableServiceProviders(providers);
        } else {
          setLeads([]);
          setAllLeads([]);
          setAvailableServiceProviders([]);
        }
      } else {
        console.error("TeamLeads: API-Fehler:", data.error);
        setError(data.error || 'Fehler beim Abrufen der Team-Leads');
      }
    } catch (err) {
      console.error("TeamLeads: Exception:", err);
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, teamId]);

  useEffect(() => {
    fetchUserSubTeamInfo();
  }, [fetchUserSubTeamInfo]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Filter anwenden
  useEffect(() => {
    if (!allLeads.length) return;
    
    let filteredLeads = [...allLeads];
    
    // Datum-Filter
    if (filters.dateFrom) {
      filteredLeads = filteredLeads.filter(lead => 
        new Date(lead.erstellt) >= new Date(filters.dateFrom)
      );
    }
    
    if (filters.dateTo) {
      filteredLeads = filteredLeads.filter(lead => 
        new Date(lead.erstellt) <= new Date(filters.dateTo + 'T23:59:59')
      );
    }
    
    // Status-Filter
    if (filters.statusPv) {
      filteredLeads = filteredLeads.filter(lead => lead.status_pv === filters.statusPv);
    }
    
    if (filters.statusWp) {
      filteredLeads = filteredLeads.filter(lead => lead.status_wp === filters.statusWp);
    }
    
    // Dienstleister-Filter
    if (filters.dienstleister) {
      filteredLeads = filteredLeads.filter(lead => lead.dienstleister === filters.dienstleister);
    }
    
    // Sortierung
    filteredLeads.sort((a, b) => {
      let aValue, bValue;
      
      if (filters.sortBy === 'name') {
        aValue = a.name || '';
        bValue = b.name || '';
      } else {
        aValue = new Date(a.erstellt);
        bValue = new Date(b.erstellt);
      }
      
      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setLeads(filteredLeads);
  }, [allLeads, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      sortBy: 'erstellt',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: '',
      statusPv: '',
      statusWp: '',
      dienstleister: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Offen': return '#3182ce';
      case 'Qualifiziert': return '#38a169';
      case 'Kein Interesse mehr': return '#e53e3e';
      case 'Widerruf': return '#d69e2e';
      case 'Telefonnummer ungültig': return '#805ad5';
      case 'Test Lead': return '#718096';
      default: return '#4a5568';
    }
  };

  const renderStatus = (status, productType, leadProduct) => {
    if (!status) return '-';
    
    const hasProduct = leadProduct && leadProduct.toLowerCase().includes(productType.toLowerCase());
    if (!hasProduct) return '-';
    
    return (
      <span style={{
        ...styles.statusBadge,
        backgroundColor: getStatusColor(status) + '20',
        color: getStatusColor(status),
        border: `1px solid ${getStatusColor(status)}30`
      }}>
        {status}
      </span>
    );
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleCustomerSelect = (customerId) => {
    navigate(`/team-leads/${teamId}/${customerId}?name=${encodeURIComponent(teamName)}`);
  };

  const handleBackToList = () => {
    navigate(`/team-leads/${teamId}?name=${encodeURIComponent(teamName)}`);
  };

  // Wenn ein Kunde ausgewählt ist, zeige die Detailansicht
  if (selectedCustomerId) {
    return (
      <div style={{
        ...styles.container,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '20px'
      }}>
        <div style={{
          ...styles.detailContent,
          maxWidth: '1200px',
          width: '100%'
        }}>
          <div style={styles.header}>
            <h2 style={styles.title}>Kundendetails</h2>
          </div>
          <CustomerDetail 
            user={user} 
            customerId={selectedCustomerId} 
            onBack={handleBackToList} 
            hideNavigation={true}
            customBackText="← Zurück zu Team-Leads"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          {userSubTeamInfo?.sub_team_name ? 
            `Sub-Team-Leads: ${teamName} (${userSubTeamInfo.sub_team_name})` : 
            `Team-Leads: ${teamName}`
          }
          <span style={{ 
            fontSize: '0.7em', 
            color: '#4a6de5', 
            marginLeft: '8px',
            fontWeight: 'normal'
          }}>
            ({leads.length})
          </span>
        </h2>
      </div>

      {/* Filter-Bereich */}
      <div style={styles.filters}>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Sortieren nach:</label>
            <select 
              style={styles.filterInput}
              value={filters.sortBy} 
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="erstellt">Erstellungsdatum</option>
              <option value="name">Name</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Reihenfolge:</label>
            <select 
              style={styles.filterInput}
              value={filters.sortOrder} 
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            >
              <option value="desc">↓ Neu-Alt</option>
              <option value="asc">↑ Alt-Neu</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Dienstleister:</label>
            <select 
              style={styles.filterInput}
              value={filters.dienstleister}
              onChange={(e) => handleFilterChange('dienstleister', e.target.value)}
            >
              <option value="">Alle Dienstleister</option>
              {availableServiceProviders.map(provider => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Von Datum:</label>
            <input 
              style={styles.filterInput}
              type="date" 
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Bis Datum:</label>
            <input 
              style={styles.filterInput}
              type="date" 
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>PV Status:</label>
            <select 
              style={styles.filterInput}
              value={filters.statusPv} 
              onChange={(e) => handleFilterChange('statusPv', e.target.value)}
            >
              <option value="">Alle Status</option>
              <option value="Offen">Offen</option>
              <option value="Qualifiziert">Qualifiziert</option>
              <option value="Kein Interesse mehr">Kein Interesse mehr</option>
              <option value="Widerruf">Widerruf</option>
              <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
              <option value="Test Lead">Test Lead</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>WP Status:</label>
            <select 
              style={styles.filterInput}
              value={filters.statusWp} 
              onChange={(e) => handleFilterChange('statusWp', e.target.value)}
            >
              <option value="">Alle Status</option>
              <option value="Offen">Offen</option>
              <option value="Qualifiziert">Qualifiziert</option>
              <option value="Kein Interesse mehr">Kein Interesse mehr</option>
              <option value="Widerruf">Widerruf</option>
              <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
              <option value="Test Lead">Test Lead</option>
            </select>
          </div>
        </div>

        <button style={styles.resetButton} onClick={resetFilters}>
          Filter zurücksetzen
        </button>
      </div>
      
      {error && <div style={styles.error}>{error}</div>}
      {loading ? (
        <div style={styles.loading}>Leads werden geladen...</div>
      ) : leads.length === 0 ? (
        <div style={styles.noData}>
          {Object.values(filters).some(filter => filter !== '' && filter !== 'erstellt' && filter !== 'desc') ? 
            'Keine Leads mit den aktuellen Filtern gefunden.' : 
            'Keine Leads gefunden.'
          }
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{...styles.th, ...styles.dateColumn}}>Erstellt</th>
                <th style={{...styles.th, ...styles.productColumn}}>Produkt</th>
                <th style={{...styles.th, ...styles.optInColumn}}>OPT IN</th>
                <th style={{...styles.th, ...styles.dienstleisterColumn}}>Dienstleister</th>
                <th style={{...styles.th, ...styles.nameColumn}}>Name</th>
                <th style={{...styles.th, ...styles.emailColumn}}>E-Mail</th>
                <th style={{...styles.th, ...styles.phoneColumn}}>Handy</th>
                <th style={{...styles.th, ...styles.statusColumn}}>Status PV</th>
                <th style={{...styles.th, ...styles.statusColumn}}>Status WP</th>
                <th style={{...styles.th, ...styles.projektColumn}}>Projekt</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr 
                  key={lead.id} 
                  style={styles.row}
                  onClick={() => handleCustomerSelect(lead.id)}
                  onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                >
                  <td style={{...styles.td, ...styles.dateColumn}} title={new Date(lead.erstellt).toLocaleString('de-DE')}>
                    {isMobile ? 
                      new Date(lead.erstellt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) :
                      new Date(lead.erstellt).toLocaleString('de-DE', {
                        dateStyle: 'short',
                        timeStyle: 'short'
                      })
                    }
                  </td>
                  <td style={{...styles.td, ...styles.productColumn}}>
                    {lead.produkt ? (
                      <div style={styles.productBadges}>
                        {lead.produkt.toLowerCase().includes('pv') && (
                          <span style={{...styles.productBadge, ...styles.pvBadge}}>PV</span>
                        )}
                        {lead.produkt.toLowerCase().includes('wp') && (
                          <span style={{...styles.productBadge, ...styles.wpBadge}}>WP</span>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td style={{...styles.td, ...styles.optInColumn}}>
                    {lead.opt_in ? 'Ja' : 'Nein'}
                  </td>
                  <td style={{...styles.td, ...styles.dienstleisterColumn}} title={lead.dienstleister}>
                    {truncateText(lead.dienstleister, isMobile ? 8 : 12)}
                  </td>
                  <td style={{...styles.td, ...styles.nameColumn}} title={`${lead.anrede ? lead.anrede + ' ' : ''}${lead.name || ''} ${lead.nachname || ''}`.trim()}>
                    {truncateText(`${lead.anrede ? lead.anrede + ' ' : ''}${lead.name || ''} ${lead.nachname || ''}`.trim(), isMobile ? 10 : 15)}
                  </td>
                  <td style={{...styles.td, ...styles.emailColumn}} title={lead.email}>
                    {truncateText(lead.email, isMobile ? 12 : 18)}
                  </td>
                  <td style={{...styles.td, ...styles.phoneColumn}} title={lead.handy}>
                    {truncateText(lead.handy, isMobile ? 8 : 12)}
                  </td>
                  <td style={{...styles.td, ...styles.statusColumn}}>
                    {renderStatus(lead.status_pv, 'pv', lead.produkt)}
                  </td>
                  <td style={{...styles.td, ...styles.statusColumn}}>
                    {renderStatus(lead.status_wp, 'wp', lead.produkt)}
                  </td>
                  <td style={{...styles.td, ...styles.projektColumn}} title={lead.projekt}>
                    {truncateText(lead.projekt, isMobile ? 8 : 12)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TeamLeads;